import type { Map, LayerSpecification } from "@iventis/mapbox-gl";
import { DataField } from "@iventis/domain-model/model/dataField";
import { createMapboxLevelFilter } from "../mapbox/filters/engine-mapbox-level-filter-helpers";
import { removeLayerFilterByType, updateLayerFilters } from "../mapbox/filters/engine-mapbox-filter-helpers";
import { createDateFilterExpression } from "../mapbox/filters/engine-mapbox-date-filter-helpers";
import { FilterType } from "../mapbox/filters/engine-mapbox-filter-types";
import { IMapboxLayer } from "./mapbox-layer";

/**
 * Basic Mapbox layer which adds functionality which most Mapbox layers will need.
 * This class is specifically for remote layers and doesn't manage the source because the vector source is handled elsewhere.
 */
export class MapboxRemoteLayer implements IMapboxLayer {
    private readonly map: Map;

    public readonly layerId: string;

    constructor(map: Map, layerId: string) {
        this.map = map;
        this.layerId = layerId;
    }

    public addLayer(layer: LayerSpecification, aboveLayerId?: string) {
        if (!this.map.getLayer(this.layerId)) {
            this.map.addLayer(layer, aboveLayerId);
        }
    }

    public removeLayer() {
        this.map.removeLayer(this.layerId);
    }

    public hideLayer(): void {
        this.map.setLayoutProperty(this.layerId, "visibility", "none");
    }

    public showLayer(): void {
        this.map.setLayoutProperty(this.layerId, "visibility", "visible");
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.map.moveLayer(this.layerId, aboveLayerId);
    }

    public updateMapLevel(level: number) {
        const levelFilter = createMapboxLevelFilter(level);
        this.updateLayerFilter(levelFilter);
    }

    public addDateFilter(dataFields: DataField[], day: number, time: number) {
        const dateFilter = createDateFilterExpression({ dataFields }, day, time, 15);
        this.updateLayerFilter(dateFilter);
    }

    public removeDateFilter() {
        this.removeLayerFilter(FilterType.Date);
    }

    private updateLayerFilter(newFilter: LayerSpecification["filter"]) {
        const currentFilter = this.map.getFilter(this.layerId);
        const updatedFilter = updateLayerFilters(currentFilter, newFilter);
        this.map.setFilter(this.layerId, updatedFilter);
    }

    private removeLayerFilter(filterToRemove: FilterType) {
        const currentFilter = this.map.getFilter(this.layerId);
        const updatedFilter = removeLayerFilterByType(currentFilter, filterToRemove);
        this.map.setFilter(this.layerId, updatedFilter);
    }
}
